import { useCanSeeAspenWeb, useImpersonatorCanSeeAspenWeb } from '@solinkcorp/authentication';
import { type ComponentType, type FC } from 'react';

function makeAspenGate(useGate: () => boolean, label: string) {
  return function withGate<P extends object>(
    Legacy: ComponentType<P>,
    Aspen: ComponentType<P>,
  ): FC<P> {
    function AspenGated(props: P) {
      return useGate() ? <Aspen {...props} /> : <Legacy {...props} />;
    }

    AspenGated.displayName = `${label}(${
      Aspen.displayName || Aspen.name || Legacy.displayName || Legacy.name || 'Component'
    })`;

    return AspenGated;
  };
}

/**
 * Pick the Aspen redesign variant when `useCanSeeAspenWeb()` is true, otherwise
 * the legacy component. Call this only at selection points (page wrappers /
 * composed HOCs) — never inside leaf components.
 *
 * Same shape as the ternary in `NavigationFrame.tsx`.
 *
 * Read the gate hook here, not at module load. A module-level read throws in tests whose auth mock does not export the other hook.
 */
export function withAspen<P extends object>(
  Legacy: ComponentType<P>,
  Aspen: ComponentType<P>,
): FC<P> {
  return makeAspenGate(useCanSeeAspenWeb, 'withAspen')(Legacy, Aspen);
}

/**
 * Pick the Aspen redesign variant for a route that renders before an impersonated user session exists.
 * Call this only at that selection point. Do not call it inside a leaf component.
 */
export function withAspenImpersonator<P extends object>(
  Legacy: ComponentType<P>,
  Aspen: ComponentType<P>,
): FC<P> {
  return makeAspenGate(useImpersonatorCanSeeAspenWeb, 'withAspenImpersonator')(Legacy, Aspen);
}
